import { useEffect, useState } from 'react';
import Http from 'services/Http';

const dismissedIds = new Set();
const openedIds = new Set();
const listeners = new Set();
const viewedListeners = new Set();
const signedListeners = new Set();
const declinedListeners = new Set();
let count = 0;
let loaded = false;
let inflight = null;
let refreshAgain = false;

function emit() {
  listeners.forEach(listener => listener(count));
}

export function isSignatureDismissed(messageId) {
  return dismissedIds.has(String(messageId));
}

export function markSignatureDismissed(messageId) {
  dismissedIds.add(String(messageId));
}

export function unmarkSignatureDismissed(messageId) {
  dismissedIds.delete(String(messageId));
}

export function isSignatureOpened(messageId) {
  return openedIds.has(String(messageId));
}

export function notifySignatureViewed(messageId) {
  const id = String(messageId);
  openedIds.add(id);
  viewedListeners.forEach(listener => listener(id));
}

export function useSignatureOpened(messageId) {
  const [opened, setOpened] = useState(() => isSignatureOpened(messageId));

  useEffect(() => {
    if (isSignatureOpened(messageId)) setOpened(true);
    return subscribeSignatureViewed(id => {
      if (String(id) === String(messageId)) setOpened(true);
    });
  }, [messageId]);

  return opened;
}

export function subscribeSignatureViewed(listener) {
  viewedListeners.add(listener);
  return () => viewedListeners.delete(listener);
}

export function notifySignatureSigned(messageId) {
  const id = String(messageId);
  signedListeners.forEach(listener => listener(id));
}

export function subscribeSignatureSigned(listener) {
  signedListeners.add(listener);
  return () => signedListeners.delete(listener);
}

export function notifySignatureDeclined(messageId) {
  const id = String(messageId);
  declinedListeners.forEach(listener => listener(id));
}

export function subscribeSignatureDeclined(listener) {
  declinedListeners.add(listener);
  return () => declinedListeners.delete(listener);
}

export function refreshPendingSignatureCount() {
  if (inflight) {
    refreshAgain = true;
    return inflight;
  }
  inflight = Http.get('/v1/mandatory/unread-count')
    .then(body => {
      count = Number(body?.data?.count ?? 0);
      loaded = true;
      emit();
      return count;
    })
    .catch(() => count)
    .finally(() => {
      inflight = null;
      if (refreshAgain) {
        refreshAgain = false;
        refreshPendingSignatureCount();
      }
    });
  return inflight;
}

export function usePendingSignatureCount() {
  const [value, setValue] = useState(count);

  useEffect(() => {
    listeners.add(setValue);
    if (!loaded) refreshPendingSignatureCount();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshPendingSignatureCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      listeners.delete(setValue);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return value;
}
